
import { useState, useCallback } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useArticleOperations } from "./useArticleOperations";
import { useTagOperations } from "./useTagOperations";
import { useAuthorOperations } from "./useAuthorOperations";

export const useArticlesLoader = () => {
  const [isLoadingAny, setIsLoadingAny] = useState(false);
  const { toast } = useToast();
  
  const { 
    articles, 
    loading: articlesLoading, 
    loadArticles: loadArticlesBase
  } = useArticleOperations();

  const {
    tags,
    loading: tagsLoading,
    loadTags: loadTagsBase
  } = useTagOperations();

  const {
    authors,
    loading: authorsLoading,
    loadAuthors: loadAuthorsBase
  } = useAuthorOperations();
  
  // Track overall loading state
  const loading = articlesLoading || tagsLoading || authorsLoading || isLoadingAny;
  
  // Wrapper functions with improved error handling and request guards
  const loadArticlesWrapper = useCallback(async (): Promise<void> => {
    if (articlesLoading || isLoadingAny) return; // Prevent concurrent requests
    
    try {
      setIsLoadingAny(true);
      await loadArticlesBase();
    } catch (error) {
      console.error("Error loading articles:", error);
      toast({
        variant: "destructive",
        title: "Error loading articles",
        description: "Failed to load articles. Please try again."
      });
    } finally {
      setIsLoadingAny(false);
    }
  }, [articlesLoading, loadArticlesBase, toast, isLoadingAny]);
  
  const loadTagsWrapper = useCallback(async (): Promise<void> => {
    if (tagsLoading || isLoadingAny) return; // Prevent concurrent requests
    
    try {
      setIsLoadingAny(true);
      await loadTagsBase();
    } catch (error) {
      console.error("Error loading tags:", error);
      toast({
        variant: "destructive",
        title: "Error loading tags",
        description: "Failed to load tags. Please try again."
      });
    } finally {
      setIsLoadingAny(false);
    }
  }, [tagsLoading, loadTagsBase, toast, isLoadingAny]);
  
  const loadAuthorsWrapper = useCallback(async (): Promise<void> => {
    if (authorsLoading || isLoadingAny) return; // Prevent concurrent requests
    
    try {
      setIsLoadingAny(true);
      await loadAuthorsBase();
    } catch (error) {
      console.error("Error loading authors:", error);
      toast({
        variant: "destructive",
        title: "Error loading authors",
        description: "Failed to load authors. Please try again."
      });
    } finally {
      setIsLoadingAny(false);
    }
  }, [authorsLoading, loadAuthorsBase, toast, isLoadingAny]);

  return {
    articles,
    tags,
    authors,
    loading,
    loadArticles: loadArticlesWrapper,
    loadTags: loadTagsWrapper,
    loadAuthors: loadAuthorsWrapper,
    articlesLoading,
    tagsLoading,
    authorsLoading
  };
};
